<template>
  <div class="container-fluid">
    <route-header>
      <h3>{{ $t('labels.nav.all_bookings') }}</h3>
      <div class="d-flex">
        <select-field v-if="auth.can['edit_users']" :name="'user'" v-model="user" class="ml-3 mb-0">
          <option value="0">{{ $t('labels.all_workers')}}</option>
          <optgroup :label="$t('labels.workers')">
            <option v-for="user in users" :key="'user' + user.id" :value="user.id">{{ user.name }}</option>
          </optgroup>
        </select-field>
      </div>
    </route-header>
    <div class="row">
      <div class="col">
        <nav-secondary-bookings></nav-secondary-bookings>
      </div>
    </div>
    <bs-card>
    <div class="row">
      <div class="col-12">
        <vue-cal
            :disable-views="['years', 'day']"
            :time-from="5 * 60"
            :time-to="20 * 60"
            :time-step="30"
            :special-hours="specialHours"
            :events="events"
            :editable-events="{ title: false, drag: this.touch, resize: this.touch, delete: true, create: this.touch }"
            :snap-to-time="15"
            events-on-month-view="short"
            :on-event-click="onEventClick"
            @event-drag-create="onEventCreate"
            @event-duration-change="onEventChange"
            @event-drop="onEventChange"
            @event-delete="onEventDelete"
            today-button
            @view-change="viewChange"
            :style="'height: ' + height + 'px'">
          <template v-slot:event="{ event }">
            <div class="vuecal__event-title" v-html="event.title" />
            <div class="vuecal__event-subtitle">
              <small>{{ event.property }}</small>
            </div>
            <div class="vuecal__event-subtitle d-flex justify-content-center">
              <avatar v-for="user in event.users" :key="'eu' + key + user.id" class="mr-1" :user="user" />
            </div>
            <div class="vuecal__event-content">
              <hr>
              {{ joinObjects(', ', 'label', event.tasks) }}
            </div>
          </template>
        </vue-cal>
      </div>
    </div>
    </bs-card>
    <bs-modal :name="'create-' + $options.name" :label="$t('labels.action_booking', { action: resource.id ? $t('labels.edit') : $t('labels.create') })" @closed="refreshEvents" class="allow-overflow">
      <booking-modal :resource="resource" @submit="submit" />
    </bs-modal>
    <bs-modal :name="'destroy-' + $options.name" :label="$t('labels.action_booking', { action: $t('labels.destroy') })" @closed="refreshEvents">
      <bs-form :destroy="$t('labels.destroy')" @submit="destruction">
        {{ $t('messages.delete', { resource: $t('labels.booking') }) }}
      </bs-form>
    </bs-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { DateTime } from 'luxon'
import { resourceControl } from '../mixins/resourceControl'
import 'vue-cal/dist/drag-and-drop.js'

export default {
  name: 'BookingCalendarView',
  mixins: [resourceControl],
  data () {
    return {
      user: null,
      from: DateTime.local().startOf('month').minus({ months: 2 }),
      to: DateTime.local().startOf('month').plus({ months: 2 }).endOf('month'),
      height: 650,
      dailyHours: {
        from: 6 * 60,
        to: 18 * 60,
        class: 'business-hours'
      },
      dateFormat: 'yyyy-MM-dd',
      dateTimeFormat: 'yyyy-MM-dd\'T\'HH:mm',
      resource: {
        property_id: null,
        multi_day: null,
        due_at: null,
        end_at: null,
        repeat: null,
        users: [],
        tasks: [],
        task_groups: [],
        description: null,
        notify_scheduled: true,
        notify_arrived: true,
        notify_left: true
      },
      events: [],
      touch: !('ontouchstart' in window)
    }
  },
  created () {
    window.addEventListener('resize', this.resizeCal)
  },
  destroyed () {
    window.removeEventListener('resize', this.resizeCal)
  },
  mounted () {
    this.resizeCal()
    if (this.$route.query.user && this.auth.can.edit_users) this.user = this.$route.query.user
    else if (!this.auth.can.edit_users) this.user = this.auth.id
    else this.user = this.resourceState.user
  },
  computed: {
    ...mapState({
      auth: state => state.auth,
      users: state => state.usersStore,
      resources: state => state.bookings.data,
      resourceState: state => state.bookingCalendarViewState
    }),
    specialHours () {
      return {
        1: this.dailyHours,
        2: this.dailyHours,
        3: this.dailyHours,
        4: this.dailyHours,
        5: this.dailyHours
      }
    }
  },
  watch: {
    user: function (val) {
      this.$store.commit('pushState', { view: this.$options.name, key: 'user', data: val })
      if (!this.loading) this.index()
    }
  },
  methods: {
    resizeCal () {
      this.height = window.innerHeight - 206
    },
    refreshEvents () {
      const list = []
      this.resources.forEach(e => {
        let start = DateTime.fromISO(e.due_at)
        let end = DateTime.fromISO(e.end_at)
        if (e.multi_day) {
          start = start.set({ hour: 5 })
          end = end.set({ hour: 20 })
        }
        let color = 'bg-blue'
        if (e.users.length > 0 && e.users[0].color) {
          color = 'bg-' + e.users[0].color
        }
        list.push({
          start: start.toFormat('yyyy-LL-dd HH:mm'),
          end: end.toFormat('yyyy-LL-dd HH:mm'),
          title: e.customer.name,
          property: e.property ? e.property.label : '',
          content: null,
          class: color,
          users: e.users,
          tasks: e.tasks,
          rID: e.id
        })
      })
      this.events = list
    },
    viewChange (event) {
      const viewFrom = DateTime.fromJSDate(event.startDate)
      const viewTo = DateTime.fromJSDate(event.endDate)
      const loadedFrom = DateTime.fromObject({ year: this.from.year, month: this.from.month, day: this.from.day })
      const loadedTo = DateTime.fromObject({ year: this.to.year, month: this.to.month, day: this.to.day })

      if (viewFrom > loadedFrom.plus({ months: 4 })) {
        this.centerDates(viewFrom)
      } else if (viewTo < loadedTo.minus({ months: 4 })) {
        this.centerDates(viewFrom)
      }
    },
    centerDates (dt) {
      this.from = DateTime.fromObject({ year: dt.year, month: dt.month }).startOf('month').minus({ months: 3 })
      this.to = DateTime.fromObject({ year: dt.year, month: dt.month }).startOf('month').plus({ months: 3 }).endOf('month')
      this.index()
    },
    onEventClick (event) {
      const resource = this._.find(this.resources, (r) => { return r.id === event.rID })
      if (resource) {
        this.$store.commit('axiosError', null)
        this.resource = JSON.parse(JSON.stringify(resource))
        if (resource.multi_day) {
          this.resource.due_at = DateTime.fromJSDate(event.start).toFormat(this.dateFormat)
          this.resource.end_at = DateTime.fromJSDate(event.end).toFormat(this.dateFormat)
        }
        this.$modal.show('create-' + this.$options.name)
      }
    },
    onEventChange (event) {
      const resource = this._.find(this.resources, (r) => { return r.id === event.event.rID })
      if (resource) {
        this.resource = JSON.parse(JSON.stringify(resource))
        this.resource.due_at = DateTime.fromJSDate(event.event.start).toFormat(this.dateTimeFormat)
        this.resource.end_at = DateTime.fromJSDate(event.event.end).toFormat(this.dateTimeFormat)
        this.submit()
      }
    },
    onEventCreate (event) {
      this.$store.commit('axiosError', null)
      this.resource.users = []
      this.resource.tasks = []
      this.resource.task_groups = []
      this.$root.clearData(this.resource)
      this.resource.due_at = DateTime.fromJSDate(event.start).toFormat(this.dateTimeFormat)
      this.resource.end_at = DateTime.fromJSDate(event.end).toFormat(this.dateTimeFormat)
      this.$modal.show('create-' + this.$options.name)
    },
    onEventDelete (event) {
      const resource = this._.find(this.resources, (r) => { return r.id === event.rID })
      if (resource) {
        this.activeId = resource.id
        this.$modal.show('destroy-' + this.$options.name)
      }
    },
    index () {
      this.loading = true
      this.$store.commit('pushFilter', { view: 'Bookings', data: { state: null } })
      this.$store.commit('pushState', { view: 'Bookings', key: 'page', data: 1 })
      this.$store.dispatch('index', {
        collection: 'Bookings',
        params: {
          from: this.from.toJSDate(),
          to: this.to.toJSDate(),
          date: this.from.toISODate(),
          user: this.resourceState.user,
          count: null
        },
        data: {
          icon: ['fal', 'calendar-alt']
        }
      }).then(() => {
        this.loading = false
        this.refreshEvents()
      })
    },
    submit () {
      this.$store.dispatch(this.resource.id ? 'update' : 'store', { collection: 'Bookings', resource: this.resource }).then(() => {
        this.$modal.hide('create-' + this.$options.name)
      })
    },
    destruction () {
      this.$store.dispatch('destroy', { collection: 'Bookings', id: this.activeId }).then(() => {
        this.$modal.hide('destroy-' + this.$options.name)
      })
    }
  }
}
</script>

<style lang="scss">

.vuecal--month-view .vuecal__event-subtitle { display: none!important }
.vuecal--month-view .vuecal__event-content { display: none }

</style>
